import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useQueryClient } from '@tanstack/react-query';
import {
  startGameUpdate,
  cancelGameUpdate,
  type GameItem,
  type UpdateJobStatus,
} from '../../api/games';
import { getSocket } from '../../api/socket';

interface Props {
  game: GameItem | null;
  onClose: () => void;
}

type Phase = 'confirm' | 'running';

function UpdateLogView({ gameId, jobState, jobError }: {
  gameId: string;
  jobState: UpdateJobStatus['state'] | null;
  jobError: string | null;
}) {
  const [lines, setLines] = useState<string[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!gameId) return;
    const socket = getSocket();

    const cb = (data: { game_id: string; line: string }) => {
      if (data.game_id !== gameId) return;
      setLines((prev) => [...prev.slice(-499), data.line]);
    };

    socket.emit('update:subscribe', { game_id: gameId });
    socket.on('games:update:log', cb);

    return () => {
      socket.emit('update:unsubscribe', { game_id: gameId });
      socket.off('games:update:log', cb);
    };
  }, [gameId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines]);

  return (
    <Box>
      {jobState === 'queued' && (
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <CircularProgress size={16} />
          <Typography variant="body2">Warte auf Update-Worker…</Typography>
        </Stack>
      )}
      {jobState === 'running' && (
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <CircularProgress size={16} />
          <Typography variant="body2">Update läuft…</Typography>
        </Stack>
      )}
      {jobState === 'succeeded' && (
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <CheckCircleIcon color="success" fontSize="small" />
          <Typography variant="body2" color="success.main">Update erfolgreich</Typography>
        </Stack>
      )}
      {jobState === 'failed' && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Update fehlgeschlagen{jobError ? `: ${jobError}` : ''}
        </Alert>
      )}

      <Box
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          bgcolor: 'action.disabledBackground',
          borderRadius: 1,
          p: 1,
          height: 300,
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {lines.map((l, i) => (
          <div key={i}>{l}</div>
        ))}
        <div ref={bottomRef} />
      </Box>
    </Box>
  );
}

export function UpdateGameDialog({ game, onClose }: Props) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>('confirm');
  const [backupBefore, setBackupBefore] = useState(true);
  const [restartAfter, setRestartAfter] = useState(true);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [jobState, setJobState] = useState<UpdateJobStatus['state'] | null>(null);
  const [jobError, setJobError] = useState<string | null>(null);

  const isActive =
    game?.status.active === 'active' || game?.status.active === 'activating';
  const isDone = jobState === 'succeeded' || jobState === 'failed';

  // Poll job status while running
  useEffect(() => {
    if (phase !== 'running' || !game) return;
    if (isDone) return;

    const poll = async () => {
      try {
        const res = await import('../../api/games').then((m) =>
          m.fetchUpdateStatus(game.id),
        );
        setJobState(res.state);
        setJobError(res.error);
        if (res.state === 'succeeded' || res.state === 'failed') {
          void qc.invalidateQueries({ queryKey: ['games'] });
        }
      } catch {
        // ignore transient errors
      }
    };

    const id = setInterval(() => void poll(), 3_000);
    void poll();
    return () => clearInterval(id);
  }, [phase, game, isDone, qc]);

  const handleStart = async () => {
    if (!game) return;
    setStartError(null);
    setStarting(true);
    try {
      await startGameUpdate(game.id, {
        backup_before: backupBefore,
        restart_after: restartAfter,
      });
      setPhase('running');
      setJobState('queued');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Update konnte nicht gestartet werden';
      setStartError(msg);
    } finally {
      setStarting(false);
    }
  };

  const handleCancel = async () => {
    if (!game) return;
    setCancelling(true);
    try {
      await cancelGameUpdate(game.id);
    } catch {
      // ignore
    } finally {
      setCancelling(false);
    }
  };

  const handleClose = () => {
    if (phase === 'running' && !isDone) return; // block close while running
    onClose();
  };

  if (!game) return null;

  return (
    <Dialog open={!!game} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <span>Game aktualisieren — {game.name}</span>
          {phase === 'running' && (
            <Chip
              label={jobState ?? 'queued'}
              color={
                jobState === 'succeeded'
                  ? 'success'
                  : jobState === 'failed'
                  ? 'error'
                  : 'info'
              }
              size="small"
              icon={
                jobState === 'succeeded' ? (
                  <CheckCircleIcon />
                ) : jobState === 'failed' ? (
                  <ErrorIcon />
                ) : undefined
              }
            />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent>
        {phase === 'confirm' && (
          <Stack spacing={2}>
            {isActive && (
              <Alert severity="warning">
                Der Server ist gerade aktiv und wird vor dem Update gestoppt.
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary">
              SteamCMD lädt die neueste Version über Steam herunter. Das kann einige
              Minuten dauern. Du kannst den Dialog schließen — das Update läuft im
              Hintergrund weiter.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={backupBefore}
                  onChange={(e) => setBackupBefore(e.target.checked)}
                />
              }
              label="Vor dem Update ein Backup erstellen"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={restartAfter}
                  onChange={(e) => setRestartAfter(e.target.checked)}
                />
              }
              label="Server nach dem Update neu starten"
            />

            {startError && <Alert severity="error">{startError}</Alert>}
          </Stack>
        )}

        {phase === 'running' && (
          <UpdateLogView
            gameId={game.id}
            jobState={jobState}
            jobError={jobError}
          />
        )}
      </DialogContent>

      <DialogActions>
        {phase === 'confirm' && (
          <>
            <Button onClick={onClose} disabled={starting}>
              Abbrechen
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleStart()}
              disabled={starting}
              startIcon={starting ? <CircularProgress size={16} /> : undefined}
            >
              Update starten
            </Button>
          </>
        )}

        {phase === 'running' && !isDone && (
          <>
            <Button color="warning" onClick={() => void handleCancel()} disabled={cancelling}>
              {cancelling ? <CircularProgress size={16} /> : 'Abbrechen'}
            </Button>
            <Button onClick={onClose} variant="outlined">
              Im Hintergrund lassen
            </Button>
          </>
        )}

        {phase === 'running' && isDone && (
          <Button variant="contained" onClick={onClose}>
            Schließen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
